import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import EmailEventType from "../models/EmailEventType";
import IEmailDocument from "../models/IEmailDocument";
import IEmailTemplateSummaryDTO from "../models/DTO/IEmailTemplateSummaryDTO";
import IOnEmailTemplateCatalogDTO from "../models/DTO/IOnEmailTemplateCatalogDTO";
import IOnGetEmailTemplateDTO from "../models/DTO/IOnGetEmailTemplateDTO";
import IOnGetEmailTemplateSampleOrdersDTO from "../models/DTO/IOnGetEmailTemplateSampleOrdersDTO";
import IOnGetEmailTemplatesDTO from "../models/DTO/IOnGetEmailTemplatesDTO";
import IOnPreviewEmailTemplateDTO from "../models/DTO/IOnPreviewEmailTemplateDTO";
import IOnSendTestEmailTemplateDTO from "../models/DTO/IOnSendTestEmailTemplateDTO";
import IOnUpsertEmailTemplateDTO from "../models/DTO/IOnUpsertEmailTemplateDTO";
import IPreviewEmailTemplateDTO from "../models/DTO/IPreviewEmailTemplateDTO";
import IUpsertEmailTemplateDTO from "../models/DTO/IUpsertEmailTemplateDTO";

/** Who is calling; every email-template endpoint is supplier-admin scoped. */
export interface IEmailTemplateAuth {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
}

/** Selector for a template slot: a workflow step or a named event. */
export interface IEmailTemplateKey {
    order_state_id?: number;
    email_event_type?: EmailEventType;
}

/** A non-2xx answer. `errors` carries the server's per-field validation messages when present. */
export class EmailTemplateApiError extends Error {
    status: number;
    errors: string[];

    constructor(message: string, status: number, errors?: string[]) {
        super(message);
        this.name = "EmailTemplateApiError";
        this.status = status;
        this.errors = errors || [];
    }
}

const request = async <T>(url: string, auth: IEmailTemplateAuth, init: RequestInit = {}): Promise<T> => {
    const headers = {
        ...buildAuthHeadersForFetch({ userModel: auth.userModel, supplierModel: auth.supplierModel }),
        ...(init.headers || {}),
    };
    const response = await fetch(url, { ...init, headers });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new EmailTemplateApiError(errorData.message || response.statusText, response.status, errorData.errors);
    }

    return response.json();
};

const keyQuery = (key: IEmailTemplateKey): Record<string, string | number> => {
    return key.order_state_id ? { order_state_id: key.order_state_id } : { email_event_type: String(key.email_event_type) };
};

/** Every slot the supplier can customize, with the state of any saved row. */
export const getEmailTemplates = async (auth: IEmailTemplateAuth): Promise<IEmailTemplateSummaryDTO[]> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.EmailTemplatesGetAll });
    const payload = await request<IOnGetEmailTemplatesDTO>(url, auth);

    return payload.templates;
};

/** Merge vars, block types and the default theme (static editor metadata). */
export const getEmailTemplateCatalog = async (auth: IEmailTemplateAuth): Promise<IOnEmailTemplateCatalogDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.EmailTemplatesCatalog });

    return request<IOnEmailTemplateCatalogDTO>(url, auth);
};

/** The editable document for a slot: the saved v2 document, a converted legacy row, or a starter. */
export const getEmailTemplateByKey = async (auth: IEmailTemplateAuth, key: IEmailTemplateKey): Promise<IOnGetEmailTemplateDTO> => {
    const url = buildRestUrlWithQuery({ endpoint: RestEndpointsEnum.EmailTemplateGetByKey, query: keyQuery(key) });

    return request<IOnGetEmailTemplateDTO>(url, auth);
};

export const upsertEmailTemplate = async (auth: IEmailTemplateAuth, dto: IUpsertEmailTemplateDTO): Promise<IEmailTemplateSummaryDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.EmailTemplateUpsert });
    const payload = await request<IOnUpsertEmailTemplateDTO>(url, auth, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dto),
    });

    return payload.template;
};

/**
 * Reset a slot to the built-in email. `key` identifies the slot: a legacy row shared by sibling
 * slots (one "getting started" row serving both invite types) is reset for that slot only.
 */
export const deleteEmailTemplate = async (auth: IEmailTemplateAuth, guid: string, key?: IEmailTemplateKey): Promise<void> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.EmailTemplateDelete,
        params: { emailTemplateGuid: guid },
        query: key ? keyQuery(key) : {},
    });

    await request<{ success: boolean }>(url, auth, { method: "DELETE" });
};

export const previewEmailTemplate = async (
    auth: IEmailTemplateAuth,
    dto: IPreviewEmailTemplateDTO
): Promise<IOnPreviewEmailTemplateDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.EmailTemplatePreview });

    return request<IOnPreviewEmailTemplateDTO>(url, auth, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dto),
    });
};

/** Render with sample data and email it to the signed-in user. */
export const sendTestEmailTemplate = async (
    auth: IEmailTemplateAuth,
    dto: IPreviewEmailTemplateDTO
): Promise<IOnSendTestEmailTemplateDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.EmailTemplateSendTest });

    return request<IOnSendTestEmailTemplateDTO>(url, auth, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dto),
    });
};

/** Orders (or only quotes) the preview can be rendered from, newest first, with an optional name filter. */
export const getEmailTemplateSampleOrders = async (
    auth: IEmailTemplateAuth,
    q?: string,
    quotesOnly?: boolean
): Promise<IOnGetEmailTemplateSampleOrdersDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.EmailTemplatesSampleOrders,
        query: { ...(q ? { q } : {}), ...(quotesOnly ? { quotes: "true" } : {}) },
    });

    return request<IOnGetEmailTemplateSampleOrdersDTO>(url, auth);
};

/** The two slots whose sample data should be quotes rather than orders. */
export const isQuoteTemplateKey = (key: IEmailTemplateKey | null | undefined): boolean => {
    return key?.email_event_type === "QUOTE_SENT" || key?.email_event_type === "QUOTE_CONVERTED_TO_ORDER";
};

/** Build the request body shared by preview and test-send. */
export const buildPreviewDto = (key: IEmailTemplateKey, document: IEmailDocument, orderGuid?: string): IPreviewEmailTemplateDTO => {
    return { document, ...key, ...(orderGuid ? { orderGuid } : {}) };
};
